import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { Bell, MessageSquare, FileText, FileCheck, MessageCircle, X, CheckCheck } from 'lucide-react';

const NotificationBell = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
      setCounts(response.data.counts || { total: 0 });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    try {
      // Navigate to the link first
      if (notification.link) {
        navigate(notification.link);
      }
      setIsOpen(false);
      // Mark as read in background
      await api.put(`/notifications/${notification.id}/read`);
      // Refresh notifications
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'invoice':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'proposal':
        return <FileCheck className="h-4 w-4 text-purple-500" />;
      case 'ticket_update':
        return <MessageCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <Bell className="h-4 w-4 text-slate-500" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      message: language === 'fr' ? 'Message' : 'Message',
      invoice: language === 'fr' ? 'Facture' : 'Invoice',
      proposal: language === 'fr' ? 'Proposition' : 'Proposal',
      ticket_update: language === 'fr' ? 'Ticket' : 'Ticket'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'fr' ? 'À l\'instant' : 'Just now';
    if (diffMins < 60) return language === 'fr' ? `Il y a ${diffMins} min` : `${diffMins}m ago`;
    if (diffHours < 24) return language === 'fr' ? `Il y a ${diffHours}h` : `${diffHours}h ago`;
    if (diffDays < 7) return language === 'fr' ? `Il y a ${diffDays}j` : `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications();
        }}
        className="relative text-slate-500 hover:text-slate-900 p-2 rounded-full hover:bg-slate-100 transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5" />
        {counts.total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full px-1">
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-900">
              {language === 'fr' ? 'Notifications' : 'Notifications'}
            </h3>
            <div className="flex items-center gap-2">
              {counts.total > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3 w-3" />
                  {language === 'fr' ? 'Tout marquer lu' : 'Mark all read'}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                <div className="animate-spin h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">
                  {language === 'fr' ? 'Aucune notification' : 'No notifications'}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0"
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-400 uppercase">
                        {getTypeLabel(notification.type)}
                      </span>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {notification.description}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDate(notification.created_at)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with counts */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-center gap-4 text-xs text-slate-500">
              {counts.messages > 0 && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3 text-blue-500" />
                  {counts.messages}
                </span>
              )}
              {counts.invoices > 0 && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3 text-green-500" />
                  {counts.invoices}
                </span>
              )}
              {counts.proposals > 0 && (
                <span className="flex items-center gap-1">
                  <FileCheck className="h-3 w-3 text-purple-500" />
                  {counts.proposals}
                </span>
              )}
              {counts.ticket_updates > 0 && (
                <span className="flex items-center gap-1">
                  <MessageCircle className="h-3 w-3 text-amber-500" />
                  {counts.ticket_updates}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
